import { BuildingIcon, SearchIcon, UserIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { contactName, listCustomers, type Contact, type Customer } from '@/api/customers'
import { useApiErrorMessage } from '@/api/errors'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'

const SEARCH_DEBOUNCE_MS = 300

/**
 * The "Kunde" search from the capture screen: find a company already in the master data and take
 * its details into the lead.
 *
 * Two kinds of choice in one list, because at a stand they are the same gesture: picking the
 * company takes the company, picking one of its people takes the company **and** that person. What
 * comes back is a copy — the lead never reads from the master record afterwards.
 */
export function CustomerPicker({
  open,
  onOpenChange,
  onPick,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPick: (customer: Customer, contact: Contact | null) => void
}) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('leadForm.picker.title')}</DialogTitle>
          <DialogDescription>{t('leadForm.picker.description')}</DialogDescription>
        </DialogHeader>
        {/* Mounted fresh each time it opens, the way the form dialogs are keyed: the second lookup
            during one capture is almost never the same company as the first, and a stale term with
            stale results on screen reads as a failed search. */}
        {open && <Search onOpenChange={onOpenChange} onPick={onPick} />}
      </DialogContent>
    </Dialog>
  )
}

function Search({
  onOpenChange,
  onPick,
}: {
  onOpenChange: (open: boolean) => void
  onPick: (customer: Customer, contact: Contact | null) => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [customers, setCustomers] = useState<Customer[] | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    const controller = new AbortController()
    listCustomers({ search: query, size: 20 }, controller.signal)
      .then((page) => {
        if (!controller.signal.aborted) {
          setCustomers(page.customers)
          setError(null)
        }
      })
      .catch((caught: unknown) => {
        if (!controller.signal.aborted) {
          setError(caught)
        }
      })
    return () => controller.abort()
  }, [query])

  function pick(customer: Customer, contact: Contact | null) {
    onPick(customer, contact)
    onOpenChange(false)
  }

  return (
    <>
      <div className="relative">
        <SearchIcon
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
          aria-hidden
        />
        <Input
          type="search"
          className="pl-8"
          autoFocus
          aria-label={t('leadForm.picker.searchLabel')}
          placeholder={t('leadForm.picker.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="max-h-80 overflow-y-auto">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage(error, t('common.unexpectedError'))}</AlertDescription>
          </Alert>
        ) : customers === null ? (
          <div className="grid gap-2">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        ) : customers.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-sm">
            {t('leadForm.picker.empty')}
          </p>
        ) : (
          <ul className="grid gap-1">
            {customers.map((customer) => (
              <li key={customer.id} className="rounded-lg border">
                <button
                  type="button"
                  className="hover:bg-accent flex w-full items-center gap-2 rounded-t-lg px-3 py-2 text-left text-sm"
                  onClick={() => pick(customer, null)}
                >
                  <BuildingIcon className="text-muted-foreground size-4 shrink-0" aria-hidden />
                  <span className="flex flex-col">
                    <span className="font-medium">{customer.companyName}</span>
                    <span className="text-muted-foreground text-xs">
                      {[customer.customerNumber, customer.address.city].filter(Boolean).join(' · ') ||
                        t('leadForm.picker.companyOnly')}
                    </span>
                  </span>
                </button>
                {customer.contacts.map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    className="hover:bg-accent flex w-full items-center gap-2 border-t px-3 py-2 pl-9 text-left text-sm"
                    onClick={() => pick(customer, contact)}
                  >
                    <UserIcon className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
                    <span className="flex flex-col">
                      <span>{contactName(contact)}</span>
                      {contact.position && (
                        <span className="text-muted-foreground text-xs">{contact.position}</span>
                      )}
                    </span>
                  </button>
                ))}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}
